import { useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';
import type { PhaseKey } from '../components/PhaseBadge';

// Status values returned by GET /projects/:projectId/phase-states (verified
// against apps/api/src/routes/phase-transitions.ts:237-244, the endpoint
// owner). `blocked` is currently a defensive branch in the API — no service
// writes phase_states.status = 'blocked' as of this commit, but the union
// keeps it for forward-compatibility.
export type PhaseTimelineStatus =
  | 'pending'
  | 'active'
  | 'completed'
  | 'awaiting_signoff'
  | 'changes_requested'
  | 'blocked';

export interface PhaseTimelineItem {
  phase: PhaseKey;
  displayName: string;
  status: PhaseTimelineStatus;
  enteredAt: string | null;
  exitedAt: string | null;
  configStatus?: string;
}

interface PhaseStatesResponse {
  currentPhase: PhaseKey | null;
  phases: PhaseTimelineItem[];
}

// Q4 — 30s polling, matching TeamPanel.tsx:47 precedent. Phase advancement
// is rare (minutes-to-hours), but the persistent ProjectLayout mount means
// without polling the stepper would freeze on intra-project nav.
const POLL_INTERVAL_MS = 30_000;

interface UseProjectPhasesResult {
  currentPhase: PhaseKey | null;
  phases: PhaseTimelineItem[];
  loading: boolean;
  error: string | null;
}

// State is tagged with the projectId it belongs to so we can DERIVE the
// data/error/loading view from `projectId` + the tagged state. This avoids
// `react-hooks/set-state-in-effect` lint errors that fire on synchronous
// setState calls inside useEffect bodies (we used to call setLoading(false)
// synchronously in the no-projectId early return; the lint plugin
// correctly flagged that as a cascading-render hazard). All state writes
// now happen ONLY inside async promise callbacks (.then / .catch).
interface DataForProject {
  projectId: string;
  data: PhaseStatesResponse;
}
interface ErrorForProject {
  projectId: string;
  error: string;
}

export function useProjectPhases(projectId: string | undefined): UseProjectPhasesResult {
  const [dataForProject, setDataForProject] = useState<DataForProject | null>(null);
  const [errorForProject, setErrorForProject] = useState<ErrorForProject | null>(null);

  useEffect(() => {
    if (!projectId) return;

    let cancelled = false;
    // R2 H2 fix — in-flight guard prevents two parallel fetches from
    // racing when the 30s interval fires while a previous (slow) fetch is
    // still resolving. Without this, the later-resolving response could
    // overwrite a newer one in setData; phase advancement is rare, so
    // skipping a tick is strictly safer than allowing the race.
    let inFlight = false;

    const fetchPhases = () => {
      if (inFlight) return;
      inFlight = true;
      apiFetch<PhaseStatesResponse>(`/projects/${projectId}/phase-states`)
        .then(v => {
          if (cancelled) return;
          setDataForProject({ projectId, data: v });
          setErrorForProject(null);
        })
        .catch(err => {
          if (cancelled) return;
          setErrorForProject({
            projectId,
            error: err instanceof Error ? err.message : 'Failed to load phases',
          });
        })
        .finally(() => {
          inFlight = false;
        });
    };

    fetchPhases();
    // Local `const id = setInterval(...)` (not a ref) — closes R2 M4
    // edge-case finding about ref assignment ordering on synchronous-throw.
    const intervalId = setInterval(fetchPhases, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [projectId]);

  // Derived view. State is only "live" when it matches the current projectId
  // — switching projects naturally clears stale data without requiring a
  // synchronous setState reset. Both sides of the projectId equality must
  // be non-undefined (else `undefined === undefined` falsely matches when
  // both projectId is undefined and the tagged state is null).
  const data =
    projectId !== undefined && dataForProject?.projectId === projectId
      ? dataForProject.data
      : null;
  const error =
    projectId !== undefined && errorForProject?.projectId === projectId
      ? errorForProject.error
      : null;
  // Loading: only when we expect a fetch (projectId defined) and neither
  // data nor error has landed for this projectId yet.
  const loading = projectId !== undefined && data === null && error === null;

  return {
    currentPhase: data?.currentPhase ?? null,
    phases: data?.phases ?? [],
    loading,
    error,
  };
}
